import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  Radar,
  ClipboardList,
  FileCheck2,
  ArrowRight,
  Ship,
  ShieldAlert,
  Anchor,
  Map,
  BrainCircuit,
  WifiOff,
  ListChecks,
  Building2,
  Scale,
  UserCheck,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react';
import BrandLogo from '../components/brand/BrandLogo';
import { useAuth } from '../context/AuthContext';
import { useHelpContext } from '../help';
import { SHIP_CHECK_SAMPLES } from '../lib/shipCheckSamples';

/**
 * Public marketing site (/welcome, and `/` for signed-out visitors).
 *
 * Positioning: VarunAIq is a maritime compliance-intelligence platform.
 * Continuous fleet readiness is the product; inspections, PSC history, class
 * status, sanctions, and corrective actions are its inputs.
 *
 * Design notes: dependency-free "stunning" treatment — CSS keyframes for the
 * ambient motion (radar sweep, float, grid drift), an IntersectionObserver
 * reveal for scroll-in sections, and a hand-built dashboard mockup as the
 * hero centerpiece (no image assets — it's all live DOM, so it stays sharp
 * on every screen and weighs nothing).
 */

/* ── Scroll-reveal: adds `lp-in` when the element enters the viewport ────── */
function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.classList.add('lp-in');
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('lp-in');
            io.unobserve(e.target);
          }
        }
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

function Reveal({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div ref={ref} className={`lp-reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

/* ── Content ──────────────────────────────────────────────────────────────── */

const STATS = [
  { value: '79,000+', label: 'vessels tracked live' },
  { value: '1.9k+', label: 'CDI 10.5 + RISQ 3.2 questions' },
  { value: '5', label: 'data sources fused per vessel' },
  { value: 'Daily', label: 'OFAC & UK sanctions sync' },
];

const PILLARS: { Icon: LucideIcon; title: string; body: string; step: string }[] = [
  {
    step: '01',
    Icon: Radar,
    title: 'Continuous fleet readiness',
    body:
      'Every vessel scored around the clock from live AIS, PSC history, class status, sanctions, and open findings, with TMSA 3 self-assessment tracked beside the fleet. You know who is inspection-ready before anyone boards.',
  },
  {
    step: '02',
    Icon: ClipboardList,
    title: 'Inspections that close the loop',
    body:
      'CDI 10.5 and RightShip RISQ 3.2 with vessel-type profiles, offline on-board capture, and AI-drafted reports. Run them externally as vettings or internally as self-inspections, and every observation becomes a tracked corrective action rather than a line lost in a PDF.',
  },
  {
    step: '03',
    Icon: FileCheck2,
    title: 'Proof on demand',
    body:
      'One-click readiness packs covering the score breakdown, certificates, PSC record, and closed findings, shareable with charterers, insurers, and auditors through a single link.',
  },
];

const STEPS = [
  {
    Icon: Map,
    title: 'Track',
    body: 'Live AIS positions, static data, and ownership stream into one enriched record per vessel.',
  },
  {
    Icon: Radar,
    title: 'Score',
    body: 'A transparent readiness score blends PSC, class, sanctions, and findings, recomputed continuously.',
  },
  {
    Icon: ClipboardList,
    title: 'Inspect',
    body: 'Board with a prefilled CDI or RISQ questionnaire, prior answers at hand, and full offline capture.',
  },
  {
    Icon: FileCheck2,
    title: 'Prove',
    body: 'Sign, generate corrective actions, and share an audit-ready readiness pack in one click.',
  },
];

const FEATURES: { Icon: LucideIcon; title: string; body: string }[] = [
  {
    Icon: Map,
    title: 'Live fleet map',
    body: 'A real-time AIS picture with weather radar and seamark overlays. Click any vessel for its photo and live sea conditions.',
  },
  {
    Icon: ShieldAlert,
    title: 'Sanctions screening',
    body: 'OFAC and UK OFSI lists synced daily and matched to hulls by IMO and MMSI, flagged before it costs you.',
  },
  {
    Icon: Anchor,
    title: 'PSC & class history',
    body: 'Port-state detentions, classification status, and an estimated Paris MoU risk profile folded into every vessel.',
  },
  {
    Icon: BrainCircuit,
    title: 'AI-drafted reports',
    body: 'Observations become regulator-format report drafts with citations. You review instead of retyping.',
  },
  {
    Icon: WifiOff,
    title: 'Offline on board',
    body: 'The full questionnaire, vessel facts, and prior findings cached to your tablet. Sync when you dock.',
  },
  {
    Icon: ListChecks,
    title: 'Corrective actions',
    body: 'Findings tracked open → verified → closed, with evidence, and the score moves when they do.',
  },
];

const PERSONAS: { Icon: LucideIcon; who: string; headline: string; body: string }[] = [
  {
    Icon: Building2,
    who: 'Ship owners & fleet managers',
    headline: 'Know your fleet is charterable before the market asks.',
    body:
      'Every vessel scored continuously, with the KPIs you answer for (observations per vetting, detentions, overdue actions, cert expiries) on one screen, and a readiness pack you can hand any charterer or insurer the moment they call.',
  },
  {
    Icon: ShieldAlert,
    who: 'DPAs & HSQE teams',
    headline: 'The audits you owe, already running.',
    body:
      'ISM internal audits on a tracked 12-month clock, TMSA 3 re-assessment reminders, and self-inspections whose every gap lands in the corrective-action queue with the question cited. Evidence of a working SMS, not a binder.',
  },
  {
    Icon: Anchor,
    who: 'Marine & technical superintendents',
    headline: 'Sleep before the vetting window.',
    body:
      'Run the real CDI or RISQ bank as a self-inspection during a port call, close the gaps before the charterer\'s inspector boards, and watch expiring class certs and overdue actions surface themselves. No more night-before spreadsheet archaeology.',
  },
  {
    Icon: Ship,
    who: 'Masters & ship staff',
    headline: 'Know what the inspector will ask, because you already answered it.',
    body:
      'The same questionnaire the vetting will use, on board, fully offline. Work through it between watches, attach photos as you go, and sync when you have signal. Boarding day holds no surprises.',
  },
  {
    Icon: Scale,
    who: 'Charterers & vetting teams',
    headline: 'Screen in seconds, not days.',
    body:
      'A single enriched record per vessel covering sanctions, detentions, ownership, and inspection history, with a defensible readiness score you can take to committee.',
  },
  {
    Icon: UserCheck,
    who: 'Inspectors',
    headline: 'Board prepared. Leave finished.',
    body:
      'Vessel briefing auto-loaded, prior answers beside every question, voice-dictated observations, and a signed report before the gangway is up.',
  },
];

/* ── Dashboard mockup (pure DOM — the hero centerpiece) ──────────────────── */

const MOCK_ROWS = [
  { name: 'EILTANK 36', sub: 'IMO 9174141 · Tanker · DE', score: 95, band: 'READY', tone: 'emerald', cls: 'Valid', actions: '0' },
  { name: 'NORDIC GRACE', sub: 'IMO 9321483 · LPG · NO', score: 88, band: 'READY', tone: 'emerald', cls: 'Valid', actions: '1' },
  { name: 'MV CORMORANT', sub: 'IMO 9285004 · Product · MT', score: 64, band: 'WATCH', tone: 'amber', cls: 'Exp 41d', actions: '3' },
  { name: 'SEA PANTHER', sub: 'IMO 9198703 · Chemical · PA', score: 38, band: 'AT RISK', tone: 'red', cls: 'Overdue', actions: '5' },
];

const TONE = {
  emerald: { text: 'text-emerald-300', dot: 'bg-emerald-400', chip: 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300' },
  amber: { text: 'text-amber-300', dot: 'bg-amber-400', chip: 'bg-amber-500/10 border-amber-500/40 text-amber-300' },
  red: { text: 'text-red-300', dot: 'bg-red-400', chip: 'bg-red-500/10 border-red-500/40 text-red-300' },
} as const;

function DashboardMock() {
  return (
    <div className="lp-float relative rounded-2xl border border-slate-700/60 bg-slate-900/80 backdrop-blur-xl shadow-2xl shadow-cyan-500/10 overflow-hidden">
      {/* Browser chrome */}
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-800 bg-slate-950/60">
        <span className="w-2.5 h-2.5 rounded-full bg-slate-700" />
        <span className="w-2.5 h-2.5 rounded-full bg-slate-700" />
        <span className="w-2.5 h-2.5 rounded-full bg-slate-700" />
        <div className="ml-3 flex-1 max-w-[240px] px-3 py-1 rounded-md bg-slate-800/80 text-[10px] text-slate-400 font-mono truncate">
          varunaiq.com/fleet
        </div>
        <span className="hidden sm:inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-cyan-300">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" /> Live
        </span>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-4 gap-2 px-4 pt-4">
        {[
          { label: 'Vessels', value: '24', tone: 'text-white' },
          { label: 'Avg readiness', value: '86', tone: 'text-cyan-300' },
          { label: 'Open actions', value: '9', tone: 'text-amber-300' },
          { label: 'Sanctions hits', value: '0', tone: 'text-emerald-300' },
        ].map((s) => (
          <div key={s.label} className="rounded-lg bg-slate-950/70 border border-slate-800 px-2.5 py-2">
            <div className={`text-base md:text-lg font-bold tabular-nums ${s.tone}`}>{s.value}</div>
            <div className="text-[9px] uppercase tracking-wider text-slate-500 truncate">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Vessel rows */}
      <div className="p-4 space-y-2">
        {MOCK_ROWS.map((r, i) => {
          const t = TONE[r.tone as keyof typeof TONE];
          return (
            <div
              key={r.name}
              className="lp-row flex items-center gap-3 rounded-xl bg-slate-950/60 border border-slate-800/90 px-3.5 py-2.5"
              style={{ animationDelay: `${0.9 + i * 0.18}s` }}
            >
              <div className="w-8 h-8 rounded-lg bg-slate-800/80 border border-slate-700/60 flex items-center justify-center shrink-0">
                <Ship className="w-4 h-4 text-slate-400" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-white truncate">{r.name}</div>
                <div className="text-[10px] text-slate-500 truncate">{r.sub}</div>
              </div>
              <div className="hidden sm:block text-[10px] text-slate-400 w-14">
                <span className={r.cls === 'Valid' ? 'text-slate-400' : t.text}>{r.cls}</span>
                <div className="text-[9px] text-slate-600">class</div>
              </div>
              <div className="hidden sm:block text-[10px] text-slate-400 w-10 text-center">
                <span className={r.actions === '0' ? 'text-slate-500' : t.text}>{r.actions}</span>
                <div className="text-[9px] text-slate-600">actions</div>
              </div>
              <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border text-[10px] font-bold tabular-nums ${t.chip}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
                {r.score} · {r.band}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── ROI estimator ────────────────────────────────────────────────────────
   Grounded on purpose. Only two levers are counted, both defensible:
   1. Superintendent time per vetting cycle (prep consolidation + report
      drafting), 1 to 1.5 working days at a fully-loaded day rate of
      ~USD 700 (≈ USD 100–120k marine-superintendent salary + overhead).
   2. Avoided repeat/failed inspections: self-inspection closes gaps
      before the vetting; commercial inspection fees run ~USD 4,000. Low
      case assumes 1 avoided per 10 vessels/yr, high case 1 per 5.
   Avoided PSC detentions and off-hire are deliberately NOT in the number:
   they're the largest saving but too situational to promise. */

const ROI = {
  dayRateUsd: 700,
  daysSavedLow: 1,
  daysSavedHigh: 1.5,
  reInspectionFeeUsd: 4000,
  avoidedPerVesselLow: 0.1,  // 1 avoided re-inspection per 10 vessels / yr
  avoidedPerVesselHigh: 0.2, // 1 per 5
};

function usd(n: number): string {
  return '$' + Math.round(n).toLocaleString('en-US');
}

function RoiEstimator() {
  const [vessels, setVessels] = useState(10);
  const [vettingsPerYear, setVettingsPerYear] = useState(2);

  const low =
    vessels * (vettingsPerYear * ROI.daysSavedLow * ROI.dayRateUsd +
      ROI.avoidedPerVesselLow * ROI.reInspectionFeeUsd);
  const high =
    vessels * (vettingsPerYear * ROI.daysSavedHigh * ROI.dayRateUsd +
      ROI.avoidedPerVesselHigh * ROI.reInspectionFeeUsd);

  return (
    <div className="rounded-3xl bg-gradient-to-b from-slate-900/70 to-slate-900/30 border border-slate-800 p-7 md:p-10">
      <div className="text-center max-w-2xl mx-auto">
        <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">What it's worth</div>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">A deliberately conservative estimate</h2>
        <p className="mt-3 text-slate-400 text-sm">
          Two levers only: superintendent time per vetting cycle, and repeat inspections
          avoided by closing gaps first. No detention savings in this number.
        </p>
      </div>

      <div className="mt-8 grid md:grid-cols-[1fr_auto] gap-8 items-center">
        <div className="space-y-5">
          <label className="block">
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-slate-300 font-medium">Vessels in your fleet</span>
              <span className="font-mono text-cyan-300 text-lg font-bold tabular-nums">{vessels}</span>
            </div>
            <input
              type="range" min={1} max={60} value={vessels}
              onChange={(e) => setVessels(Number(e.target.value))}
              aria-label="Vessels in your fleet"
              className="mt-2 w-full accent-cyan-400"
            />
          </label>
          <label className="block">
            <div className="text-sm text-slate-300 font-medium mb-2">Vetting inspections per vessel per year</div>
            <div className="flex gap-2">
              {[1, 2, 3].map((n) => (
                <button
                  key={n} type="button" onClick={() => setVettingsPerYear(n)}
                  className={`min-h-[40px] px-5 rounded-lg border text-sm font-semibold transition-all duration-150 active:scale-[0.98] ${
                    vettingsPerYear === n
                      ? 'bg-cyan-950/50 border-cyan-500 text-cyan-200'
                      : 'bg-slate-900/60 border-slate-700 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </label>
        </div>

        <div className="text-center md:text-right md:min-w-[260px]">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Estimated saving / year</div>
          <div className="mt-1 text-4xl md:text-5xl font-bold tabular-nums bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent">
            {usd(low)}–{usd(high)}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            ≈ {usd(low / vessels)}–{usd(high / vessels)} per vessel
          </div>
        </div>
      </div>

      <div className="mt-8 pt-5 border-t border-slate-800 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-[12px] text-slate-500 leading-relaxed">
        <div>
          <span className="text-slate-400 font-medium">Counted:</span> {ROI.daysSavedLow}–{ROI.daysSavedHigh} superintendent
          days saved per vetting cycle (prep consolidation + report drafting) at a fully-loaded
          day rate of {usd(ROI.dayRateUsd)}; one avoided repeat inspection per {Math.round(1 / ROI.avoidedPerVesselHigh)}–{Math.round(1 / ROI.avoidedPerVesselLow)} vessels
          per year at a {usd(ROI.reInspectionFeeUsd)} inspection fee.
        </div>
        <div>
          <span className="text-slate-400 font-medium">Not counted:</span> avoided PSC detentions and
          off-hire (typically tens of thousands of dollars per event), charter opportunities kept,
          insurance outcomes. If any of those land even once, they dwarf this figure, and we'd rather
          you be surprised in that direction.
        </div>
      </div>
      <p className="mt-3 text-[11px] text-slate-600 text-center">
        An estimate, not a quote. Your rates and vetting cadence will differ.
      </p>
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────────────────────── */

export default function LandingPage() {
  useHelpContext('landing');
  const { user, role } = useAuth();
  const canStart = !!user && (role === 'inspector' || role === 'admin');
  const [scrolled, setScrolled] = useState(false);
  const [heroImo, setHeroImo] = useState('');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#070C18] text-white overflow-x-clip">
      <style>{`
        .lp-reveal { opacity: 0; transform: translateY(22px); transition: opacity .7s cubic-bezier(.2,.8,.2,1), transform .7s cubic-bezier(.2,.8,.2,1); }
        .lp-reveal.lp-in { opacity: 1; transform: none; }
        @keyframes lp-sweep { to { transform: rotate(360deg); } }
        .lp-sweep { animation: lp-sweep 9s linear infinite; transform-origin: 50% 50%; }
        @keyframes lp-floaty { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-10px) } }
        .lp-float { animation: lp-floaty 7s ease-in-out infinite; }
        @keyframes lp-row-in { from { opacity: 0; transform: translateX(14px); } to { opacity: 1; transform: none; } }
        .lp-row { opacity: 0; animation: lp-row-in .6s cubic-bezier(.2,.8,.2,1) forwards; }
        @keyframes lp-grid-drift { from { background-position: 0 0; } to { background-position: 0 56px; } }
        .lp-grid {
          background-image: linear-gradient(rgba(148,163,184,.07) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(148,163,184,.07) 1px, transparent 1px);
          background-size: 56px 56px;
          mask-image: radial-gradient(ellipse 90% 65% at 50% 0%, black 35%, transparent 78%);
          -webkit-mask-image: radial-gradient(ellipse 90% 65% at 50% 0%, black 35%, transparent 78%);
          animation: lp-grid-drift 14s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .lp-sweep, .lp-float, .lp-grid { animation: none; }
          .lp-row { opacity: 1; animation: none; }
        }
      `}</style>

      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-[#070C18]/85 backdrop-blur-xl border-b border-slate-800/70' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <BrandLogo size="sm" tone="dark" />
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-400">
            <a href="#product" className="hover:text-white transition-colors">Product</a>
            <a href="#how" className="hover:text-white transition-colors">How it works</a>
            <a href="#teams" className="hover:text-white transition-colors">Who it's for</a>
            <Link to="/help" className="hover:text-white transition-colors">Help</Link>
            <Link to="/check" className="whitespace-nowrap font-medium text-cyan-300 hover:text-cyan-200 transition-colors">
              Free ship check
            </Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to={user ? '/' : '/login'}
              className="hidden sm:block text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              {user ? 'Open app' : 'Sign in'}
            </Link>
            <Link
              to="/vetting/demo"
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-sm font-semibold shadow-md shadow-cyan-500/25 transition-all duration-150 active:scale-[0.98]"
            >
              Live demo
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="relative pt-28 md:pt-36 pb-10 md:pb-16">
        {/* Ambient background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 lp-grid" />
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[560px] rounded-full bg-cyan-500/10 blur-[130px]" />
          <div className="absolute top-24 right-[-180px] w-[520px] h-[420px] rounded-full bg-blue-700/15 blur-[120px]" />
          <div className="absolute top-60 left-[-160px] w-[420px] h-[380px] rounded-full bg-cyan-400/5 blur-[100px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            <div className="lg:col-span-6">
              <Reveal>
                <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-950/60 border border-cyan-800/50 text-cyan-300 text-[11px] font-semibold uppercase tracking-widest">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                  Maritime compliance intelligence
                </div>
              </Reveal>
              <Reveal delay={80}>
                <h1 className="mt-5 text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight">
                  Every vessel
                  <br />
                  <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-blue-400 bg-clip-text text-transparent">
                    inspection-ready.
                  </span>
                  <br />
                  Provably.
                </h1>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-6 text-lg md:text-xl text-slate-300/90 leading-relaxed max-w-xl">
                  VarunAIq scores your whole fleet continuously. Live AIS, port-state history,
                  class, sanctions, and open findings fuse into one number you can defend, and
                  one pack you can hand to any charterer.
                </p>
              </Reveal>
              <Reveal delay={240}>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <Link
                    to="/vetting/demo"
                    className="group px-6 py-3.5 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold text-base shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-150 active:scale-[0.98]"
                  >
                    <Sparkles className="w-5 h-5" />
                    See it in action
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                  <Link
                    to={canStart ? '/vetting/inspections/new' : user ? '/' : '/register/viewer'}
                    className="px-6 py-3.5 inline-flex items-center gap-2 rounded-xl bg-white/5 hover:bg-white/10 border border-slate-700 hover:border-slate-500 text-white font-semibold text-base transition-all duration-150 active:scale-[0.98]"
                  >
                    {canStart ? 'Start an inspection' : user ? 'Open your fleet' : 'Create free account'}
                  </Link>
                </div>
              </Reveal>
              <Reveal delay={320}>
                <div className="mt-4 text-[13px] text-slate-500">
                  The demo is fully public. No account, no card, no calls.
                </div>
              </Reveal>
              {/* Instant value: check any ship right from the hero. Samples are
                  IMOs verified to resolve richly (photo + risk profile). */}
              <Reveal delay={400}>
                <div className="mt-7 rounded-2xl bg-slate-900/50 border border-slate-800 p-4 max-w-xl">
                  <div className="text-[11px] uppercase tracking-widest text-cyan-400 font-semibold">
                    Free ship check, no account
                  </div>
                  <form
                    className="mt-2.5 flex gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (/^\d{7}$/.test(heroImo)) window.location.assign(`/check?imo=${heroImo}`);
                    }}
                  >
                    <input
                      value={heroImo}
                      onChange={(e) => setHeroImo(e.target.value.replace(/[^\d]/g, '').slice(0, 7))}
                      inputMode="numeric"
                      placeholder="IMO number, e.g. 9481233"
                      aria-label="IMO number"
                      className="flex-1 min-w-0 min-h-[46px] px-3.5 bg-slate-950 border border-slate-700 rounded-xl text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                    <button
                      type="submit"
                      className="min-h-[46px] px-4 rounded-xl bg-white/5 hover:bg-white/10 border border-cyan-700/60 hover:border-cyan-500 text-cyan-200 text-sm font-semibold whitespace-nowrap transition-all duration-150 active:scale-[0.98]"
                    >
                      Check
                    </button>
                  </form>
                  <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
                    <span>Try:</span>
                    {SHIP_CHECK_SAMPLES.map((s) => (
                      <Link
                        key={s.imo}
                        to={`/check?imo=${s.imo}`}
                        className="px-2 py-1 rounded-full bg-slate-800/70 border border-slate-700 hover:border-cyan-600/60 text-slate-300 hover:text-cyan-200 transition-colors"
                      >
                        {s.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>

            {/* Product mockup */}
            <div className="lg:col-span-6 relative">
              <Reveal delay={200}>
                {/* Radar halo behind the mock */}
                <div className="absolute -inset-8 md:-inset-14 pointer-events-none opacity-60">
                  <svg viewBox="0 0 400 400" className="w-full h-full">
                    <circle cx="200" cy="200" r="190" stroke="rgba(34,211,238,.14)" strokeWidth="1" fill="none" />
                    <circle cx="200" cy="200" r="150" stroke="rgba(34,211,238,.10)" strokeWidth="1" fill="none" />
                    <circle cx="200" cy="200" r="110" stroke="rgba(34,211,238,.08)" strokeWidth="1" fill="none" />
                    <g className="lp-sweep">
                      <path d="M200 200 L200 10 A190 190 0 0 1 334 66 Z" fill="rgba(34,211,238,.06)" />
                      <line x1="200" y1="200" x2="334" y2="66" stroke="rgba(34,211,238,.35)" strokeWidth="1.2" />
                    </g>
                  </svg>
                </div>
                <DashboardMock />
                {/* Floating score card */}
                <div className="lp-float absolute -bottom-6 -left-3 md:-left-8 rounded-xl bg-slate-900/95 border border-emerald-500/30 shadow-xl shadow-emerald-500/10 px-4 py-3 backdrop-blur" style={{ animationDelay: '1.2s' }}>
                  <div className="text-[9px] uppercase tracking-widest text-slate-500">Readiness pack</div>
                  <div className="flex items-center gap-2 mt-1">
                    <FileCheck2 className="w-4 h-4 text-emerald-300" />
                    <span className="text-sm font-bold text-white">EILTANK 36</span>
                    <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] font-bold">
                      95 · READY
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">Shared with charterer · expires in 14 days</div>
                </div>
              </Reveal>
            </div>
          </div>

          {/* ── Stats band ─────────────────────────────────────────────── */}
          <Reveal delay={120}>
            <div className="mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-4 rounded-2xl border border-slate-800 bg-slate-900/40 backdrop-blur divide-x divide-y md:divide-y-0 divide-slate-800 overflow-hidden">
              {STATS.map((s) => (
                <div key={s.label} className="px-6 py-6 text-center">
                  <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent tabular-nums">
                    {s.value}
                  </div>
                  <div className="text-[11px] md:text-xs text-slate-400 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Pillars ─────────────────────────────────────────────────────── */}
      <section id="product" className="relative py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">The platform</div>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">
                From signal to signed report
              </h2>
              <p className="mt-4 text-slate-400 text-lg">
                Not another checklist app. A single system that watches, scores, inspects, and proves.
              </p>
            </div>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.step} delay={i * 110}>
                <div className="group relative h-full rounded-2xl bg-slate-900/50 border border-slate-800 p-7 overflow-hidden hover:border-cyan-700/60 hover:shadow-[0_0_40px_rgba(6,182,212,0.12)] transition-all duration-300">
                  <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-cyan-500/5 blur-2xl group-hover:bg-cyan-500/10 transition-colors duration-300" />
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-700/40 flex items-center justify-center">
                      <p.Icon className="w-6 h-6 text-cyan-300" />
                    </div>
                    <span className="font-mono text-xs text-slate-600">{p.step}</span>
                  </div>
                  <h3 className="mt-5 text-xl font-bold">{p.title}</h3>
                  <p className="mt-2.5 text-[15px] text-slate-400 leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────────────────── */}
      <section id="how" className="relative py-20 md:py-24 border-y border-slate-800/60 bg-slate-950/40">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">How it works</div>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">
                Four steps. One auditable loop.
              </h2>
            </div>
          </Reveal>
          <div className="mt-14 relative grid md:grid-cols-4 gap-8 md:gap-5">
            {/* Connector line (desktop) */}
            <div className="hidden md:block absolute top-7 left-[12%] right-[12%] h-px bg-gradient-to-r from-cyan-500/0 via-cyan-500/40 to-cyan-500/0" />
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 120}>
                <div className="relative text-center md:px-2">
                  <div className="relative z-10 mx-auto w-14 h-14 rounded-2xl bg-slate-900 border border-cyan-700/50 shadow-[0_0_24px_rgba(6,182,212,0.15)] flex items-center justify-center">
                    <s.Icon className="w-6 h-6 text-cyan-300" />
                  </div>
                  <div className="mt-4 text-lg font-bold">{s.title}</div>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed max-w-[260px] mx-auto">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Feature grid ────────────────────────────────────────────────── */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Reveal>
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">Capabilities</div>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">
                Everything the vetting desk asks for.{' '}
                <span className="text-slate-500">Already answered.</span>
              </h2>
            </div>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 90}>
                <div className="h-full rounded-xl bg-slate-900/40 border border-slate-800/90 p-5 hover:bg-slate-900/70 hover:border-slate-700 transition-all duration-200">
                  <f.Icon className="w-5 h-5 text-cyan-300" />
                  <h3 className="mt-3 text-[15px] font-bold">{f.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Personas ────────────────────────────────────────────────────── */}
      <section id="teams" className="relative py-20 md:py-24 border-y border-slate-800/60 bg-slate-950/40">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">Who it's for</div>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">
                Built for everyone who answers for the ship
              </h2>
              <p className="mt-4 text-slate-400">
                From the boardroom to the bridge: one platform, one version of the truth,
                each role seeing the piece they're accountable for.
              </p>
            </div>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {PERSONAS.map((p, i) => (
              <Reveal key={p.who} delay={i * 110}>
                <div className="h-full rounded-2xl bg-gradient-to-b from-slate-900/70 to-slate-900/30 border border-slate-800 p-7 hover:border-cyan-800/60 transition-colors duration-300">
                  <p.Icon className="w-6 h-6 text-cyan-300" />
                  <div className="mt-4 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{p.who}</div>
                  <h3 className="mt-2 text-xl font-bold leading-snug">{p.headline}</h3>
                  <p className="mt-3 text-[15px] text-slate-400 leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Shore office — the compliance cadence you already owe ─────────
           Grounded in verified sources: ISM Code §12 internal audits at
           ≤12-month intervals; OCIMF recommends re-taking TMSA every 12
           months; superintendents answer for observation close-out. */}
      <section className="relative py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto">
              <div className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">For owners &amp; the shore office</div>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">
                The audits you already owe, on one platform
              </h2>
              <p className="mt-4 text-slate-400">
                External vettings are events. Readiness is a process, and most of it is
                compliance you're required to run anyway.
              </p>
            </div>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            <Reveal delay={0}>
              <div className="h-full rounded-2xl bg-gradient-to-b from-slate-900/70 to-slate-900/30 border border-slate-800 p-7 hover:border-cyan-800/60 transition-colors duration-300">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Self-inspections · ISM Code §12</div>
                <h3 className="mt-2 text-xl font-bold leading-snug">Internal audits every 12 months, with a gap list instead of a binder</h3>
                <p className="mt-3 text-[15px] text-slate-400 leading-relaxed">
                  Run the same CDI or RISQ question bank internally, offline on board. Every No
                  becomes an open corrective action with the question cited, assigned, tracked,
                  and closed before the charterer's inspector boards. The fleet dashboard flags
                  any vessel past its 12-month cadence.
                </p>
              </div>
            </Reveal>
            <Reveal delay={110}>
              <div className="h-full rounded-2xl bg-gradient-to-b from-slate-900/70 to-slate-900/30 border border-slate-800 p-7 hover:border-cyan-800/60 transition-colors duration-300">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">TMSA 3 · OCIMF cadence</div>
                <h3 className="mt-2 text-xl font-bold leading-snug">Your annual self-assessment, tracked, not rediscovered</h3>
                <p className="mt-3 text-[15px] text-slate-400 leading-relaxed">
                  OCIMF recommends re-taking the self-assessment every 12 months. VarunAIq keeps
                  the 13 elements and stages live year-round, shows the average stage on your
                  fleet dashboard, and tells you when the re-assessment is due.
                </p>
              </div>
            </Reveal>
            <Reveal delay={220}>
              <div className="h-full rounded-2xl bg-gradient-to-b from-slate-900/70 to-slate-900/30 border border-slate-800 p-7 hover:border-cyan-800/60 transition-colors duration-300">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">KPIs superintendents answer for</div>
                <h3 className="mt-2 text-xl font-bold leading-snug">Observations per inspection, actions aging, and PSC, all on one screen</h3>
                <p className="mt-3 text-[15px] text-slate-400 leading-relaxed">
                  The numbers your office quotes (observations per vetting, overdue corrective
                  actions, detentions, cert expiries) computed continuously from your own fleet's
                  data instead of assembled by hand across systems the night before a review.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── ROI estimator ─────────────────────────────────────────────────
           Deliberately conservative: only time and re-inspection fees are
           counted; avoided detentions/off-hire are named but never added to
           the headline number. All assumptions shown inline. */}
      <section className="relative py-20 md:py-24 border-t border-slate-800/60 bg-slate-950/40">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Reveal>
            <RoiEstimator />
          </Reveal>
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────────────────────────────── */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden border border-cyan-800/40 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950/60 px-8 md:px-16 py-14 md:py-20 text-center">
              <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[560px] h-[300px] rounded-full bg-cyan-500/15 blur-[100px] pointer-events-none" />
              <h2 className="relative text-3xl md:text-5xl font-bold tracking-tight leading-tight">
                Make your fleet{' '}
                <span className="bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent">
                  provably ready.
                </span>
              </h2>
              <p className="relative mt-5 text-lg text-slate-400 max-w-xl mx-auto">
                Walk the full inspection workflow in the public demo, or create an account and
                add your first vessels in minutes.
              </p>
              <div className="relative mt-8 flex flex-wrap justify-center items-center gap-3">
                <Link
                  to="/vetting/demo"
                  className="px-7 py-3.5 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-150 active:scale-[0.98]"
                >
                  <Sparkles className="w-4 h-4" />
                  Try the live demo
                </Link>
                <Link
                  to={user ? '/' : '/register/viewer'}
                  className="px-7 py-3.5 inline-flex items-center gap-2 rounded-xl bg-white/5 hover:bg-white/10 border border-slate-700 hover:border-slate-500 text-white font-semibold transition-all duration-150 active:scale-[0.98]"
                >
                  {user ? 'Open your fleet' : 'Get started free'}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <BrandLogo size="sm" tone="dark" />
          <nav className="flex flex-wrap items-center justify-center gap-x-7 gap-y-2 text-sm text-slate-500">
            <a href="#product" className="hover:text-slate-300 transition-colors">Product</a>
            <a href="#how" className="hover:text-slate-300 transition-colors">How it works</a>
            <Link to="/vetting/demo" className="hover:text-slate-300 transition-colors">Demo</Link>
            <Link to="/help" className="hover:text-slate-300 transition-colors">Help</Link>
            <Link to="/check" className="hover:text-slate-300 transition-colors">Free ship check</Link>
            <Link to="/login" className="hover:text-slate-300 transition-colors">Sign in</Link>
          </nav>
          <div className="text-xs text-slate-600">
            © {new Date().getFullYear()} VarunAIq · varunaiq.com
          </div>
        </div>
      </footer>
    </div>
  );
}
